import { useState, useEffect } from 'react';
import { Plus, Eye, EyeOff, Trash2, Edit, RefreshCw, Save, X, Shield, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Permission definitions with German labels
// Note: 'dashboard' is always visible to all users as a start page
const PERMISSION_GROUPS = [
  {
    title: 'Dashboard & Analytics',
    permissions: [
      { key: 'dashboard', label: 'Dashboard anzeigen', alwaysEnabled: true, hint: 'Startseite - immer sichtbar' },
      { key: 'analytics', label: 'Analytics anzeigen' },
    ]
  },
  {
    title: 'Fahrzeuge',
    permissions: [
      { key: 'cars', label: 'Fahrzeuge anzeigen' },
      { key: 'cars_create', label: 'Fahrzeuge erstellen' },
      { key: 'cars_edit', label: 'Fahrzeuge bearbeiten' },
      { key: 'cars_delete', label: 'Fahrzeuge löschen' },
    ]
  },
  {
    title: 'Kunden & Verträge',
    permissions: [
      { key: 'customers', label: 'Kunden verwalten' },
      { key: 'contracts', label: 'Verträge verwalten' },
      { key: 'inquiries', label: 'Anfragen/Chat verwalten' },
      { key: 'test_drive', label: 'Probefahrten verwalten' },
    ]
  },
  {
    title: 'Content',
    permissions: [
      { key: 'blog', label: 'Blog verwalten' },
      { key: 'newsletter', label: 'Newsletter verwalten' },
      { key: 'media', label: 'Medien verwalten' },
    ]
  },
  {
    title: 'System',
    permissions: [
      { key: 'settings', label: 'Einstellungen ändern' },
    ]
  }
];

export default function AdminAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [passwordsLoading, setPasswordsLoading] = useState({});
  
  // Form state
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    permissions: {
      dashboard: true,
      analytics: true,
      cars: true,
      cars_create: true,
      cars_edit: true,
      cars_delete: true,
      test_drive: true,
      customers: true,
      contracts: true,
      inquiries: true,
      blog: true,
      newsletter: true,
      media: true,
      settings: false
    }
  });

  useEffect(() => {
    loadAccounts();
  }, []);

  const getToken = () => localStorage.getItem('token');

  const loadAccounts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin-accounts/`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAccounts(data);
      } else if (response.status === 403) {
        toast.error('Nur der Hauptadmin hat Zugriff auf diese Seite');
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
      toast.error('Fehler beim Laden der Accounts');
    } finally {
      setLoading(false);
    }
  };

  const loadPassword = async (accountId) => {
    setPasswordsLoading(prev => ({ ...prev, [accountId]: true }));
    try {
      const response = await fetch(`${API_URL}/api/admin-accounts/${accountId}/password`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVisiblePasswords(prev => ({ ...prev, [accountId]: data.password || '(nicht verfügbar)' }));
      }
    } catch (error) {
      toast.error('Fehler beim Laden des Passworts');
    } finally {
      setPasswordsLoading(prev => ({ ...prev, [accountId]: false }));
    }
  };

  const generatePassword = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin-accounts/generate-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, password: data.password }));
        toast.success('Passwort generiert');
      }
    } catch (error) {
      toast.error('Fehler beim Generieren');
    }
  };

  const handleCreate = async () => {
    if (!formData.username || !formData.password) {
      toast.error('Benutzername und Passwort erforderlich');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin-accounts/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Account erstellt');
        setShowCreateModal(false);
        resetForm();
        loadAccounts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Erstellen');
      }
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    }
  };

  const handleUpdate = async () => {
    if (!editingAccount) return;

    try {
      const updateData = {
        email: formData.email,
        permissions: formData.permissions
      };
      if (formData.password) {
        updateData.password = formData.password;
      }

      const response = await fetch(`${API_URL}/api/admin-accounts/${editingAccount.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        toast.success('Account aktualisiert');
        setEditingAccount(null);
        resetForm();
        loadAccounts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Aktualisieren');
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const response = await fetch(`${API_URL}/api/admin-accounts/${deleteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (response.ok) {
        toast.success('Account gelöscht');
        loadAccounts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Löschen');
      }
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleteId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      permissions: {
        dashboard: true,
        analytics: true,
        cars: true,
        cars_create: true,
        cars_edit: true,
        cars_delete: true,
        test_drive: true,
        customers: true,
        contracts: true,
        inquiries: true,
        blog: true,
        newsletter: true,
        media: true,
        settings: false
      }
    });
  };

  const startEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      username: account.username,
      email: account.email || '',
      password: '',
      permissions: {
        dashboard: account.permissions?.dashboard ?? true,
        analytics: account.permissions?.analytics ?? true,
        cars: account.permissions?.cars ?? true,
        cars_create: account.permissions?.cars_create ?? true,
        cars_edit: account.permissions?.cars_edit ?? true,
        cars_delete: account.permissions?.cars_delete ?? true,
        test_drive: account.permissions?.test_drive ?? true,
        customers: account.permissions?.customers ?? true,
        contracts: account.permissions?.contracts ?? true,
        inquiries: account.permissions?.inquiries ?? true,
        blog: account.permissions?.blog ?? true,
        newsletter: account.permissions?.newsletter ?? true,
        media: account.permissions?.media ?? true,
        settings: account.permissions?.settings ?? false
      }
    });
  };

  const togglePassword = (accountId) => {
    if (visiblePasswords[accountId]) {
      setVisiblePasswords(prev => ({ ...prev, [accountId]: null }));
    } else {
      loadPassword(accountId);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl">Admin-Accounts</h2>
          <p className="text-sm text-[#666666]">Verwalten Sie Benutzerkonten und Berechtigungen</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowCreateModal(true); }}
          className="btn-primary flex items-center gap-2"
          data-testid="create-account-btn"
        >
          <Plus size={18} />
          Neuer Account
        </Button>
      </div>

      {/* Accounts List */}
      <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F9F9F7]">
            <tr>
              <th className="text-left p-4 font-medium">Benutzername</th>
              <th className="text-left p-4 font-medium">E-Mail</th>
              <th className="text-left p-4 font-medium">Passwort</th>
              <th className="text-left p-4 font-medium">Status</th>
              <th className="text-left p-4 font-medium">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((account) => (
              <tr key={account.id} className="border-t border-[#E5E5E5]">
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    {account.is_main_admin && (
                      <Shield size={16} className="text-gold" title="Hauptadmin" />
                    )}
                    <span className="font-medium">{account.username}</span>
                  </div>
                </td>
                <td className="p-4 text-[#666666]">{account.email || '-'}</td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm">
                      {visiblePasswords[account.id] || '••••••••'}
                    </span>
                    {!account.is_main_admin && (
                      <button
                        onClick={() => togglePassword(account.id)}
                        className="p-1 hover:bg-[#F9F9F7] rounded"
                        disabled={passwordsLoading[account.id]}
                      >
                        {passwordsLoading[account.id] ? (
                          <RefreshCw size={16} className="animate-spin" />
                        ) : visiblePasswords[account.id] ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  {account.is_main_admin ? (
                    <span className="px-2 py-1 bg-gold/20 text-gold text-xs rounded-full font-medium">
                      Hauptadmin
                    </span>
                  ) : account.is_active ? (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                      Aktiv
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full font-medium">
                      Inaktiv
                    </span>
                  )}
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    {!account.is_main_admin && (
                      <>
                        <button
                          onClick={() => startEdit(account)}
                          className="p-2 hover:bg-[#F9F9F7] rounded"
                          title="Bearbeiten"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteId(account.id)}
                          className="p-2 hover:bg-red-50 text-red-500 rounded"
                          title="Löschen"
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingAccount) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#E5E5E5] flex items-center justify-between">
              <h3 className="font-heading text-xl">
                {editingAccount ? 'Account bearbeiten' : 'Neuer Admin-Account'}
              </h3>
              <button
                onClick={() => { setShowCreateModal(false); setEditingAccount(null); resetForm(); }}
                className="p-2 hover:bg-[#F9F9F7] rounded"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-2">Benutzername *</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="input-premium w-full"
                    placeholder="z.B. lehrling1"
                    disabled={!!editingAccount}
                    data-testid="account-username"
                  />
                </div>
                <div>
                  <label className="overline block mb-2">E-Mail</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-premium w-full"
                    placeholder="email@example.com"
                    data-testid="account-email"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="overline block mb-2">
                  Passwort {editingAccount ? '(leer lassen = nicht ändern)' : '*'}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="input-premium flex-1"
                    placeholder="Passwort eingeben oder generieren"
                    data-testid="account-password"
                  />
                  <Button
                    type="button"
                    onClick={generatePassword}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <RefreshCw size={16} />
                    Generieren
                  </Button>
                </div>
              </div>

              {/* Permissions */}
              <div>
                <h4 className="font-heading text-lg mb-4">Berechtigungen</h4>
                <p className="text-sm text-[#666666] mb-4">
                  Das Dashboard ist für alle Benutzer als Startseite sichtbar. Nicht erlaubte Bereiche werden ausgegraut angezeigt.
                </p>
                <div className="space-y-4">
                  {PERMISSION_GROUPS.map((group) => (
                    <div key={group.title} className="p-4 bg-[#F9F9F7] rounded-lg">
                      <h5 className="font-medium mb-3">{group.title}</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {group.permissions.map((perm) => (
                          <label
                            key={perm.key}
                            className={`flex items-center gap-2 ${perm.alwaysEnabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                            title={perm.hint || ''}
                          >
                            <div className="relative">
                              <input
                                type="checkbox"
                                checked={perm.alwaysEnabled ? true : formData.permissions[perm.key]}
                                disabled={perm.alwaysEnabled}
                                onChange={(e) => !perm.alwaysEnabled && setFormData({
                                  ...formData,
                                  permissions: {
                                    ...formData.permissions,
                                    [perm.key]: e.target.checked
                                  }
                                })}
                                className="sr-only peer"
                              />
                              <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                                perm.alwaysEnabled 
                                  ? 'bg-gray-400 border-gray-400' 
                                  : 'border-[#E5E5E5] peer-checked:bg-green-500 peer-checked:border-green-500'
                              }`}>
                                {(perm.alwaysEnabled || formData.permissions[perm.key]) && (
                                  <Check size={14} className="text-white" />
                                )}
                              </div>
                            </div>
                            <span className="text-sm">{perm.label}</span>
                            {perm.alwaysEnabled && <span className="text-xs text-[#999999]">(immer)</span>}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-[#E5E5E5] flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowCreateModal(false); setEditingAccount(null); resetForm(); }}
              >
                Abbrechen
              </Button>
              <Button
                onClick={editingAccount ? handleUpdate : handleCreate}
                className="btn-primary flex items-center gap-2"
              >
                <Save size={18} />
                {editingAccount ? 'Speichern' : 'Erstellen'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Account löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Dieser Account wird unwiderruflich gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
